import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import { formatRub } from "./PlatformDashboardParts";
import {
  GRANDFATHER_PLAN_CODES,
  offerPlanNames,
  planIdsAll,
  planIdsByCodes,
  planIdsPublic,
  resolveClubOfferIds,
} from "./billingUi";
import s from "./platformAdmin.module.css";

export default function PlatformPlanAccess({ plans }) {
  const [clinics, setClinics] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    offer_plan_ids: [],
    offer_until: "",
    crm_monthly_price_rub: "",
    crm_price_locked: false,
    apply_price: false,
  });

  const load = () => {
    platformAPI
      .getClinics()
      .then((res) => setClinics(res.clinics || []))
      .catch((e) =>
        setErr(e.response?.data?.error || "Не удалось загрузить клубы"),
      );
  };

  useEffect(() => {
    load();
  }, []);

  const activePlans = useMemo(
    () => (plans || []).filter((p) => p.is_active !== false),
    [plans],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return clinics;
    return clinics.filter((c) => {
      const hay =
        `${c.name} ${c.subdomain} ${c.owner?.email || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [clinics, q]);

  const toggleAll = (on) => {
    if (!on) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(filtered.map((c) => c.id)));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setPreset = (kind) => {
    if (kind === "grandfather") {
      setForm({
        ...form,
        offer_plan_ids: planIdsByCodes(activePlans, GRANDFATHER_PLAN_CODES),
      });
      return;
    }
    if (kind === "landing") {
      setForm({ ...form, offer_plan_ids: planIdsPublic(activePlans) });
      return;
    }
    if (kind === "all") {
      setForm({ ...form, offer_plan_ids: planIdsAll(activePlans) });
      return;
    }
    setForm({ ...form, offer_plan_ids: [] });
  };

  const apply = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    const tenant_ids = [...selected];
    if (!tenant_ids.length) {
      setErr("Отметьте клубы в списке ниже");
      return;
    }
    if (!form.offer_plan_ids.length) {
      setErr("Отметьте хотя бы один план для оплаты");
      return;
    }
    setBusy(true);
    try {
      const body = {
        tenant_ids,
        // Бэкенд ещё знает catalog — UI его не показывает.
        // Явный offer_plan_ids — источник правды.
        billing_catalog: "current",
        billing_catalog_until: form.offer_until || null,
        offer_plan_ids: form.offer_plan_ids,
      };
      if (form.apply_price) {
        body.crm_monthly_price_rub =
          form.crm_monthly_price_rub === ""
            ? null
            : Number(form.crm_monthly_price_rub);
        body.crm_price_locked = Boolean(form.crm_price_locked);
      }
      const res = await platformAPI.bulkUpdateClinicBillingAccess(body);
      setMsg(`Обновлено клубов: ${res.updated || tenant_ids.length}`);
      setSelected(new Set());
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.billingSetup}>
      <div className={s.billingGuide}>
        <strong>Планы в оплате</strong>
        <p>
          Это не лендинг. Здесь задаёте, какие планы клуб увидит при продлении
          или смене тарифа. Текущий план клуба при этом не меняется.
        </p>
      </div>

      {msg ? <div className={s.alertSuccess}>{msg}</div> : null}
      {err ? <div className={s.alertError}>{err}</div> : null}

      <form className={s.billingStepCard} onSubmit={apply}>
        <div className={s.billingStepHead}>
          <span className={s.billingStepNum}>1</span>
          <div>
            <h3 className={s.billingStepTitle}>Какие планы открыть</h3>
            <p className={s.billingStepHint}>
              Пресеты только проставляют галки — можно править вручную.
            </p>
          </div>
        </div>

        <div className={s.moduleAccessRow}>
          <button
            type="button"
            className={s.moduleAccessBtn}
            onClick={() => setPreset("grandfather")}
          >
            Стартовый · Расш. · Макс.
          </button>
          <button
            type="button"
            className={s.moduleAccessBtn}
            onClick={() => setPreset("landing")}
          >
            Как на лендинге
          </button>
          <button
            type="button"
            className={s.moduleAccessBtn}
            onClick={() => setPreset("all")}
          >
            Все планы
          </button>
          <button
            type="button"
            className={s.moduleAccessBtn}
            onClick={() => setPreset("none")}
          >
            Снять
          </button>
        </div>

        <div className={s.billingPlanChecks}>
          {activePlans.map((p) => {
            const id = String(p.id);
            const checked = form.offer_plan_ids.includes(id);
            return (
              <label key={p.id} className={s.billingPlanCheck}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...form.offer_plan_ids, id]
                      : form.offer_plan_ids.filter((x) => x !== id);
                    setForm({ ...form, offer_plan_ids: next });
                  }}
                />
                <span>
                  {p.name}
                  <small>
                    {formatRub(p.monthly_price_rub)} ₽/мес
                    {p.is_public ? " · на лендинге" : ""}
                  </small>
                </span>
              </label>
            );
          })}
        </div>

        <label className={s.field}>
          <span>Действует до (необязательно)</span>
          <input
            type="date"
            value={form.offer_until}
            onChange={(e) =>
              setForm({ ...form, offer_until: e.target.value })
            }
          />
          <small className={s.fieldHint}>
            После даты в оплате останутся только планы с лендинга.
          </small>
        </label>

        <div className={s.billingOptionBlock}>
          <label className={s.billingOptionRow}>
            <input
              type="checkbox"
              checked={form.apply_price}
              onChange={(e) =>
                setForm({ ...form, apply_price: e.target.checked })
              }
            />
            <div>
              <strong>Заодно сменить цену продления</strong>
              <span>Без галки цены клубов не трогаем</span>
            </div>
          </label>
          {form.apply_price ? (
            <div className={s.formGrid}>
              <label className={s.field}>
                <span>Цена в месяц, ₽</span>
                <input
                  type="number"
                  min="0"
                  value={form.crm_monthly_price_rub}
                  onChange={(e) =>
                    setForm({ ...form, crm_monthly_price_rub: e.target.value })
                  }
                />
              </label>
              <label className={s.billingOptionRow}>
                <input
                  type="checkbox"
                  checked={form.crm_price_locked}
                  onChange={(e) =>
                    setForm({ ...form, crm_price_locked: e.target.checked })
                  }
                />
                <div>
                  <strong>Зафиксировать</strong>
                  <span>Не подтягивать цену из карточки плана</span>
                </div>
              </label>
            </div>
          ) : null}
        </div>

        <div className={s.billingApplyBar}>
          <span>
            Планов: <b>{form.offer_plan_ids.length}</b> · клубов:{" "}
            <b>{selected.size}</b>
          </span>
          <button
            type="submit"
            className={s.heroActionBtn}
            disabled={busy || !selected.size || !form.offer_plan_ids.length}
          >
            {busy ? "Сохраняю…" : "Применить"}
          </button>
        </div>
      </form>

      <section className={s.billingStepCard}>
        <div className={s.billingStepHead}>
          <span className={s.billingStepNum}>2</span>
          <div>
            <h3 className={s.billingStepTitle}>Клубы</h3>
            <p className={s.billingStepHint}>
              Отметьте, кому применить набор сверху.
            </p>
          </div>
        </div>

        <div className={s.billingClubToolbar}>
          <input
            className={s.billingSearch}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск: название, subdomain, email"
          />
          <button
            type="button"
            className={s.heroActionBtnGhost}
            onClick={() => toggleAll(true)}
          >
            Все в списке
          </button>
          <button
            type="button"
            className={s.heroActionBtnGhost}
            onClick={() => toggleAll(false)}
          >
            Снять
          </button>
        </div>

        <div className={s.billingClubList}>
          {filtered.map((c) => {
            const ids = resolveClubOfferIds(c, activePlans);
            const names = offerPlanNames(ids, activePlans);
            const currentName =
              c.saasPlan?.name ||
              c.billing?.plan_label ||
              activePlans.find((p) => String(p.id) === String(c.saas_plan_id))
                ?.name ||
              null;
            const until =
              c.billing_catalog_until || c.billing?.billing_catalog_until || "";
            const price =
              c.crm_monthly_price_rub ?? c.billing?.monthly_price_rub;
            const locked = Boolean(
              c.crm_price_locked ?? c.billing?.price_locked,
            );
            const checked = selected.has(c.id);
            return (
              <label
                key={c.id}
                className={`${s.billingClubRow} ${
                  checked ? s.billingClubRowOn : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(c.id)}
                />
                <div className={s.billingClubMain}>
                  <div className={s.billingClubTitle}>
                    <Link to={`/admin/tenants/${c.id}`}>{c.name}</Link>
                    <span className={s.billingClubSub}>{c.subdomain}</span>
                  </div>
                  <div className={s.billingClubMeta}>
                    {currentName ? (
                      <span className={`${s.statusPill} ${s.statusActive}`}>
                        Сейчас: {currentName}
                      </span>
                    ) : null}
                    <span>
                      В оплате:{" "}
                      {names.length ? names.join(" · ") : "не задано"}
                    </span>
                    {until ? <span>до {until}</span> : null}
                    {price != null ? (
                      <span>
                        {formatRub(price)} ₽
                        {locked ? " · фикс." : ""}
                      </span>
                    ) : null}
                  </div>
                </div>
              </label>
            );
          })}
          {!filtered.length ? (
            <p className={s.emptyHint}>Нет клубов по фильтру</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
